// @ts-check
import axios from 'axios';

/** @typedef {import('./schema').components['schemas']} Schemas */
/** @typedef {Schemas['NextMeetingsResponse']} NextMeetingsResponse */

/**
 * @param {number} entityId
 * @returns {Promise<NextMeetingsResponse>}
 */
export async function getNextMeetings(entityId) {
  const { data } = await axios.get(
    '/api/engagement-graph-nodes/next-meetings/',
    {
      params: { entity_id: entityId },
    }
  );
  return data;
}

/**
 * @param {{ entityId: number, grain?: string, days?: number }} options
 * @returns {Promise<any>}
 */
export async function getTimeAllocation({
  entityId,
  grain = 'week',
  days = 180,
}) {
  const { data } = await axios.get(
    '/api/engagement-graph-nodes/time-allocation/',
    {
      params: { entity_id: entityId, grain, days },
    }
  );
  return data;
}

/**
 * @param {{ scopeEntityId: number, groupBy?: string }} options
 * @returns {Promise<any>}
 */
export async function getScopedOpsSummary({
  scopeEntityId,
  groupBy = 'portfolio',
}) {
  const { data } = await axios.get('/api/engagement-graph-nodes/ops-summary/', {
    params: { scope_entity_id: scopeEntityId, group_by: groupBy },
  });
  return data;
}

/**
 * @param {number} actionId
 * @returns {Promise<void>}
 */
export async function completeAction(actionId) {
  await axios.post(`/api/graph-actions/${actionId}/complete/`, {
    incomplete: false,
  });
}

/**
 * @param {number} actionId
 * @returns {Promise<void>}
 */
export async function dropAction(actionId) {
  await axios.delete(`/api/graph-actions/${actionId}/`);
}

/**
 * @param {number} nodeId
 * @param {'resolved' | 'dropped'} userStatus
 * @returns {Promise<void>}
 */
export async function setConstraintStatus(nodeId, userStatus) {
  await axios.patch(`/api/engagement-graph-nodes/${nodeId}/`, {
    user_status: userStatus,
  });
}

/**
 * @param {number} nodeId
 * @param {boolean} prioritized
 * @returns {Promise<any>}
 */
export async function setPriority(nodeId, prioritized) {
  const { data } = await axios.post(
    `/api/engagement-graph-nodes/${nodeId}/set-priority/`,
    { prioritized }
  );
  return data;
}

/**
 * @param {number} nodeId
 * @param {string} text
 * @param {boolean} [complete]
 * @returns {Promise<{update: {id: number, node_id: number, observed_at: string, text: string, user_name: string | null, created_at: string}, status: string}>}
 */
export async function logUpdate(nodeId, text, complete = false) {
  const { data } = await axios.post(
    `/api/engagement-graph-nodes/${nodeId}/log-update/`,
    { text, complete }
  );
  return data;
}

export async function logPageView(ids, event = 'impression') {
  const { data } = await axios.post(
    '/api/engagement-graph-nodes/log-page-view/',
    { ids, event }
  );
  return data;
}

/**
 * @param {number[]} ids
 * @param {boolean} prioritized
 * @returns {Promise<{updated: {id: number, prioritized: boolean, prioritized_at: string | null, prioritized_by: string | null}[]}>}
 */
export async function setPriorityBulk(ids, prioritized) {
  const { data } = await axios.post(
    '/api/engagement-graph-nodes/set-priority-bulk/',
    { ids, prioritized }
  );
  return data;
}
